import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["cellConfig"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React from 'react';
import { compose } from 'recompose';
import { defaultMemoize } from 'reselect';
import _head from 'lodash/head';
import _property from 'lodash/property';
import _keyBy from 'lodash/keyBy';
import _mapValues from 'lodash/mapValues';
import _isEmpty from 'lodash/isEmpty';
import _last from 'lodash/last';
import _reduce from 'lodash/reduce';
import _get from 'lodash/get';
import _curry from 'lodash/curry';
import _isArray from 'lodash/isArray';
import _startsWith from 'lodash/startsWith';

// Constants
import { EMPTY_ARRAY, EMPTY_OBJECT } from '@tekion/tekion-base/app.constants';
import { REMOVE_ACTION, ADD_CHILD_ACTION_PREFIX, NESTING_KEY, PRINT_ACTION } from './constants/general';

// Renderers
import MultiCellRenderer from './cellRenderers/MultiCellRenderer';
var getComponentFromConfig = compose(function (config) {
  return config.getComponent();
}, _head);

/**
 * Converts the custom cell config to react table cell config by assigning the appropriate cell component to the Cell prop
 * 1. In case of multiple cell configs, MultiCellRenderer
 * 2. In case of single the component mentioned in the cell configuration
 */
var mapColumnToTableColumnConfig = function mapColumnToTableColumnConfig(_ref) {
  var _ref$cellConfig = _ref.cellConfig,
    cellConfig = _ref$cellConfig === void 0 ? EMPTY_ARRAY : _ref$cellConfig,
    rest = _objectWithoutProperties(_ref, _excluded);
  var multipleCellConfigsPresent = cellConfig.length > 1;
  if (multipleCellConfigsPresent) {
    return _objectSpread(_objectSpread({}, rest), {}, {
      Cell: function Cell(props) {
        return /*#__PURE__*/React.createElement(MultiCellRenderer, props);
      }
    });
  }
  return _objectSpread(_objectSpread({}, rest), {}, {
    Cell: getComponentFromConfig(cellConfig)
  });
};
var getColumnConfigsForTable = function getColumnConfigsForTable() {
  var columns = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_ARRAY;
  return columns.map(mapColumnToTableColumnConfig);
};
var getCellConfigurationMap = function getCellConfigurationMap() {
  var columns = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_ARRAY;
  var columnMapById = _keyBy(columns, 'id');
  return _mapValues(columnMapById, 'cellConfig');
};
export var mapFieldToTableProps = function mapFieldToTableProps(_ref2) {
  var _ref2$columns = _ref2.columns,
    columns = _ref2$columns === void 0 ? EMPTY_ARRAY : _ref2$columns;
  return {
    columnConfigs: getColumnConfigsForTable(columns),
    cellConfigurationMap: getCellConfigurationMap(columns)
  };
};
export var getColumnIdFromAction = _property('payload.id');
export var getCellOnAction = _property('tdProps.rest.onAction');
export var getMapCellPropsToComponentProps = _property('column.mapCellPropsToComponentProps');
export var getColumnId = _property('column.id');
var getLastChild = function getLastChild(value) {
  if (value[NESTING_KEY]) {
    return getLastChild(value[NESTING_KEY]);
  }
  return _isArray(value) ? _last(value) : value;
};

/**
 * Checks whether the last row in the table is empty or not.
 * @param {Array} value: A list of row values enterd in the table
 */
export var isLastRowEmpty = compose(_isEmpty, getLastChild, _last);

/**
 * Checks whether to add a new row in the table while onchange.
 * Returns true when the following conditions are met
 * 1. When the row being edited is at the base level and is not a nested row.
 * 2. When the row being edited is the last row.
 * 3. Before the edit the row was empty.
 * @param {Object} actionPayload: payload of the action object coming from the cell being edited.
 * @param {Array} value: A list of table row values entered by the user.
 */
export var shouldAddNewRow = function shouldAddNewRow() {
  var actionPayload = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var error = arguments.length > 2 ? arguments[2] : undefined;
  // eslint-disable-line no-unused-vars
  if (actionPayload.actionType === REMOVE_ACTION.id || actionPayload.actionType === PRINT_ACTION.id || _startsWith(actionPayload.actionType, ADD_CHILD_ACTION_PREFIX)) return false;
  var nestingPath = actionPayload.nestingPath || EMPTY_ARRAY;
  var isFirstNestingLevel = nestingPath.length === 1;
  var rowIndex = _head(nestingPath);
  var isCurrentRowIsLast = rowIndex + 1 === value.length;
  return isFirstNestingLevel && isCurrentRowIsLast && !isLastRowEmpty(value);
};
var createdExpandedObject = _curry(function (subRowsKey, acc, value, index) {
  var subRow = _get(value, subRowsKey);
  if (subRow) {
    acc[index] = _reduce(subRow, createdExpandedObject(subRowsKey), {}); // eslint-disable-line no-param-reassign
  }

  return acc;
});
export var getExpandedFromValues = defaultMemoize(function (values, subRowsKey) {
  return _reduce(values, createdExpandedObject(subRowsKey), {});
});
export var isLastRow = function isLastRow(row) {
  return _get(row, 'isLastRow', false);
};